import {
    useEffect,
    useRef,
    useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Lottie from "lottie-react";

import {
    journeyAction,
    journeyStatus,
} from "../../../api/journey.api";

import { setJourney } from "../../../store/journeySlice";

import type { AppDispatch } from "../../../store";

import loader from "../../../assets/POS Accept Payment Loader.json";

import MobileFrame from "../../../components/MobileFrame";
import LoanSummary from "../../dashboard/loansummary";
import { ApiError } from "../../../api/ApiError";

const MANDATE_REDIRECT_URL =
    "https://prod-native.cleverpe.com/ndashboard/mandate/callback";

const MandateCallback = () => {
    const navigate = useNavigate();

    const dispatch =
        useDispatch<AppDispatch>();

    const [error, setError] =
        useState<string | null>(null);

    const [isRetrying, setIsRetrying] =
        useState(false);

    /*
     * =========================================================
     * VERIFIED
     * =========================================================
     *
     * Once the mandate status check succeeds, we show
     * LoanSummary in place instead of navigating away.
     *
     * The user only leaves this screen (to /ndashboard)
     * after they explicitly tap Continue on LoanSummary.
     */
    const [isVerified, setIsVerified] =
        useState(false);

    /*
     * Prevent duplicate initial API call
     * caused by React StrictMode.
     */
    const hasFired =
        useRef(false);

    /*
     * =========================================================
     * VERIFY MANDATE STATUS
     * =========================================================
     *
     * Backend is the source of truth.
     *
     * Success:
     *      update Redux
     *      show LoanSummary (mandate entry point)
     *
     * Failure:
     *      show Try Again
     */
    const verifyMandate = async () => {
        try {
            setError(null);

            const response = await journeyStatus("MANDATE");

            if (!response?.data?.journey) {
                throw new Error(
                    "Mandate status response did not contain journey data."
                );
            }

            // Backend is the source of truth
            dispatch(
                setJourney(
                    response.data.journey
                )
            );

            const mandateStatus =
                response.data.metadata?.status;

            /*
             * Only consider the mandate successful
             * when backend explicitly returns Success.
             */
            if (
                // mandateStatus?.toUpperCase() !== "INITIATED"
                mandateStatus?.toUpperCase() !== "SUCCESS"
            ) {
                throw new Error(
                    "Your repayment mandate wasn't set up. Please try again."
                );
            }

            /*
             * Mandate successfully verified.
             * Now show Loan Summary.
             */
            setIsVerified(true);

        } catch (error) {

            let message =
                "Unable to verify mandate status. Please try again.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setError(message);
        }
    };


    /*
     * =========================================================
     * GENERATE NEW MANDATE LINK
     * =========================================================
     *
     * Used when the user clicks "Try Again".
     *
     * This does NOT send the user back to the
     * banking details form.
     *
     * It generates a fresh E-Mandate link and
     * redirects directly to Caprion.
     */
    const generateMandateAndRedirect =
        async () => {

            try {
                setIsRetrying(true);

                const response =
                    await journeyAction({
                        subStep: "E_MANDATE",
                        action: "GENERATE",
                        payload: {
                            redirectUrl:
                                MANDATE_REDIRECT_URL,
                        },
                    });

                /*
                 * Update Redux with the latest
                 * journey returned by backend.
                 */
                if (
                    response?.data?.journey
                ) {
                    dispatch(
                        setJourney(
                            response.data.journey
                        )
                    );
                }

                /*
                 * Extract the new mandate link.
                 */
                const mandateLink =
                    response?.data?.metadata
                        ?.mandateLink;

                if (!mandateLink) {
                    throw new Error(
                        "Mandate link was not returned."
                    );
                }

                /*
                 * Redirect directly to Caprion.
                 */
                window.location.href =
                    mandateLink;

            } catch (error) {

                let message =
                    "Unable to generate a new mandate link. Please try again.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setError(message);

                setIsRetrying(false);
            }
        };


    /*
     * =========================================================
     * INITIAL CALLBACK VERIFICATION
     * =========================================================
     *
     * Runs once when Caprion redirects the user
     * back to this page.
     */
    useEffect(() => {

        if (hasFired.current) {
            return;
        }

        hasFired.current = true;

        verifyMandate();

    }, []);


    /*
     * =========================================================
     * RETRY
     * =========================================================
     *
     * Retry means:
     *
     * Generate a fresh E-Mandate link
     *        ↓
     * Redirect to Caprion
     */
    const handleRetry = () => {

        if (isRetrying) {
            return;
        }

        generateMandateAndRedirect();
    };


    /*
     * =========================================================
     * VERIFIED — SHOW LOAN SUMMARY
     * =========================================================
     *
     * User leaves this screen only by tapping Continue.
     */
    if (isVerified) {
        return (
            <LoanSummary
                entryPoint="mandate"
                onBack={() =>
                    navigate(
                        "/ndashboard",
                        { replace: true }
                    )
                }
                onContinue={() =>
                    navigate(
                        "/ndashboard",
                        { replace: true }
                    )
                }
            />
        );
    }


    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background blobs */}
                <div className="absolute -top-24 -left-20 h-64 w-64 rounded-full bg-purple-200 opacity-20 blur-3xl" />

                <div className="absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-blue-200 opacity-20 blur-3xl" />

                <div className="relative z-10 flex h-full w-full flex-col px-6 pt-6 pb-4">

                    {/* Header */}
                    <div className="flex items-center justify-end">
                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90"
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-1 flex-col items-center justify-center">

                        {error ? (
                            <div className="w-full text-center">

                                {/* Error Icon */}
                                <div
                                    className="
                                    mx-auto
                                    flex
                                    h-10
                                    w-10
                                    items-center
                                    justify-center
                                    rounded-full
                                    bg-red-50
                                "
                                >
                                    <span className="text-lg font-bold text-red-500">
                                        !
                                    </span>
                                </div>

                                <h2
                                    className="
                                    mt-3
                                    text-base
                                    font-semibold
                                    text-gray-900
                                "
                                >
                                    Mandate Failed
                                </h2>

                                <p
                                    className="
                                    mt-1.5
                                    text-xs
                                    leading-relaxed
                                    text-gray-500
                                "
                                >
                                    {error}
                                </p>

                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center">

                                <Lottie
                                    animationData={loader}
                                    loop
                                    autoplay
                                    className="h-40 w-40"
                                />

                                <p
                                    className="
                                    mt-2
                                    text-center
                                    text-[13px]
                                    font-medium
                                    text-gray-500
                                "
                                >
                                    Verifying your E-Mandate...
                                </p>

                            </div>
                        )}

                    </div>

                    {/* Bottom Action */}
                    {error && (
                        <button
                            type="button"
                            onClick={handleRetry}
                            disabled={isRetrying}
                            className="
                            mb-2
                            flex
                            w-full
                            items-center
                            justify-center
                            gap-2
                            rounded-full
                            bg-gradient-to-r
                            from-indigo-500
                            to-blue-400
                            py-3
                            text-sm
                            font-semibold
                            text-white
                            transition
                            hover:opacity-95
                            active:scale-[0.98]
                            disabled:cursor-not-allowed
                            disabled:opacity-60
                        "
                        >
                            {isRetrying ? (
                                <>
                                    <span
                                        className="
                                        h-4
                                        w-4
                                        animate-spin
                                        rounded-full
                                        border-2
                                        border-white/40
                                        border-t-white
                                    "
                                    />
                                    Redirecting...
                                </>
                            ) : (
                                "Try Again"
                            )}
                        </button>
                    )}

                    {/* Footer */}
                    <p
                        className="
                        mt-2
                        text-center
                        text-[11px]
                        font-semibold
                        tracking-wide
                        text-gray-400
                    "
                    >
                        CleverPe
                    </p>

                </div>
            </div>
        </MobileFrame>
    );
};

export default MandateCallback;